/** 全站 UI 工具：复制兼容 iOS / Android */

export function toast(msg) {
  try {
    window.dispatchEvent(new CustomEvent("fx-toast", { detail: String(msg || "") }));
  } catch {
    /* ignore */
  }
}

export function shortAddr(addr) {
  const s = String(addr || "");
  if (s.length < 12) return s || "—";
  return s.slice(0, 6) + "…" + s.slice(-4);
}

function isTouchLike() {
  try {
    return (
      (navigator.maxTouchPoints || 0) > 0 ||
      /Android|iPhone|iPad|iPod|Mobile|WebView/i.test(navigator.userAgent || "") ||
      (window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
    );
  } catch {
    return false;
  }
}

/**
 * 同步 execCommand 复制（触屏必须优先：await 后常丢失用户手势）
 * 不用负坐标 / display:none，部分安卓会直接失败
 */
function copyViaExec(text) {
  const value = String(text || "");
  if (!value || typeof document === "undefined") return false;

  const ta = document.createElement("textarea");
  ta.value = value;
  ta.setAttribute("readonly", "");
  ta.setAttribute("aria-hidden", "true");
  ta.style.cssText =
    "position:fixed;top:0;left:0;width:2px;height:2px;padding:0;margin:0;border:0;" +
    "opacity:0.01;outline:none;box-shadow:none;background:transparent;" +
    "z-index:99999;-webkit-user-select:text;user-select:text;font-size:16px;";
  // iOS：防止键盘弹起缩放
  document.body.appendChild(ta);

  const prev = document.activeElement;
  const editable = document.body.contentEditable;
  try {
    // iOS Safari 偶发需要短暂可编辑上下文
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent || "")) {
      try {
        document.body.contentEditable = "true";
      } catch {
        /* ignore */
      }
    }
    ta.focus({ preventScroll: true });
    ta.select();
    ta.setSelectionRange(0, value.length);

    let ok = false;
    try {
      ok = !!document.execCommand("copy");
    } catch {
      ok = false;
    }

    // 再试一次 Selection API（少数 WebView）
    if (!ok && typeof window.getSelection === "function") {
      try {
        const range = document.createRange();
        range.selectNodeContents(ta);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        ta.setSelectionRange(0, value.length);
        ok = !!document.execCommand("copy");
        sel.removeAllRanges();
      } catch {
        /* ignore */
      }
    }
    return ok;
  } catch {
    return false;
  } finally {
    try {
      document.body.contentEditable = editable;
    } catch {
      /* ignore */
    }
    try {
      ta.remove();
    } catch {
      /* ignore */
    }
    try {
      if (prev && typeof prev.focus === "function") {
        prev.focus({ preventScroll: true });
      }
    } catch {
      /* ignore */
    }
  }
}

/**
 * 复制文本。触屏：先同步 exec，再尝试 Clipboard API。
 * @returns {Promise<boolean>}
 */
export async function copyText(text, okMsg = "已复制", failMsg = "复制失败，请长按地址手动复制") {
  const value = String(text ?? "");
  if (!value) {
    toast(failMsg);
    return false;
  }

  const touch = isTouchLike();
  const hasClip =
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function" &&
    (typeof window === "undefined" || window.isSecureContext !== false);

  // 1) 触屏 / 无 Clipboard API：同步 exec（保留用户手势）
  if (touch || !hasClip) {
    if (copyViaExec(value)) {
      toast(okMsg);
      return true;
    }
  }

  // 2) 现代 Clipboard API
  if (hasClip) {
    try {
      await navigator.clipboard.writeText(value);
      toast(okMsg);
      return true;
    } catch {
      /* fall through */
    }
  }

  // 3) 桌面 Clipboard 失败后再 exec
  if (copyViaExec(value)) {
    toast(okMsg);
    return true;
  }

  toast(failMsg);
  return false;
}

export function fillCountLabel(note) {
  const s = String(note || "");
  const m = s.match(/要填[：:]\s*(\d+)\s*项/) || s.match(/fill\s*[：:]\s*(\d+)/i);
  if (!m) return "";
  return "要填 " + m[1] + " 项";
}

/** 相对时间：5s / 3m / 2h / 1d */
export function formatRel(ms) {
  if (!ms) return "";
  const sec = Math.floor((Date.now() - Number(ms)) / 1000);
  if (sec < 60) return sec + "s";
  const min = Math.floor(sec / 60);
  if (min < 60) return min + "m";
  const hr = Math.floor(min / 60);
  if (hr < 24) return hr + "h";
  return Math.floor(hr / 24) + "d";
}

export function formatUsd(n) {
  if (n == null || !isFinite(n) || n <= 0) return "—";
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return "$" + (n / 1e3).toFixed(2) + "K";
  if (n >= 1) return "$" + n.toFixed(2);
  return "$" + Number(n).toPrecision(3);
}

/** @param {number} [digits=1] Home 用 1 位，Monitor 用 2 位 */
export function formatPct(n, digits = 1) {
  if (n == null || !isFinite(n)) return "—";
  const d = Math.max(0, Math.min(4, Number(digits) || 0));
  return (Math.round(Number(n) * 10 ** d) / 10 ** d).toFixed(d) + "%";
}

export function flapUrl(token) {
  const t = String(token || "").toLowerCase();
  return /^0x[a-f0-9]{40}$/.test(t) ? "https://flap.sh/bnb/" + t : "https://flap.sh";
}

/** 紧凑数量：池深 / 报价副单位（股票底小额多留几位） */
export function formatAmt(n) {
  if (n == null || !isFinite(n)) return "—";
  if (n >= 10000) return n.toFixed(0);
  if (n >= 1000) return n.toFixed(1);
  if (n >= 100) return n.toFixed(2);
  if (n >= 1) return n.toFixed(4);
  if (n >= 0.001) return n.toFixed(5);
  if (n >= 1e-6) return n.toFixed(6);
  return n.toExponential(2);
}

/** MM-DD HH:mm */
export function formatTime(ms) {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  if (!isFinite(d.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    pad(d.getMonth() + 1) +
    "-" +
    pad(d.getDate()) +
    " " +
    pad(d.getHours()) +
    ":" +
    pad(d.getMinutes())
  );
}

/**
 * 侧栏相对时间（带中英后缀）
 * @param {number} ms
 * @param {(zh:string,en:string)=>string} t lang.t
 */
export function formatAgo(ms, t) {
  const translate = typeof t === "function" ? t : (zh) => zh;
  const ts = Number(ms);
  if (!ts || !Number.isFinite(ts)) return translate("刚刚", "just now");
  const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (sec < 60) return sec + translate("秒前", "s ago");
  const min = Math.floor(sec / 60);
  if (min < 60) return min + translate("分钟前", "m ago");
  const hr = Math.floor(min / 60);
  if (hr < 24) return hr + translate("小时前", "h ago");
  return Math.floor(hr / 24) + translate("天前", "d ago");
}

/**
 * RPC / 节点报错归类：throttle | down | generic
 * @param {unknown} err
 */
export function classifyRpcError(err) {
  const raw = String(
    err && typeof err === "object" && "message" in err
      ? err.message
      : err || ""
  );
  const s = raw.toLowerCase();
  if (
    /429|rate.?limit|throttl|too many|limit exceeded|timeout|timed?\s*out|etimedout|deadline|abort/.test(
      s
    )
  ) {
    return "throttle";
  }
  if (
    /failed to fetch|network|econn|enotfound|502|503|504|bad gateway|rpc|node|upstream|cloudflare|521|522|523/.test(
      s
    )
  ) {
    return "down";
  }
  return "generic";
}

/**
 * 监听状态条文案（含节点限速/不可用）
 * @param {unknown} err
 * @param {(zh:string,en:string)=>string} t
 */
export function humanizeRpcError(err, t) {
  const translate = typeof t === "function" ? t : (zh) => zh;
  const kind = classifyRpcError(err);
  if (kind === "throttle") {
    return translate(
      "节点限速或超时，正在换节点重试…",
      "RPC throttled / timed out — retrying…"
    );
  }
  if (kind === "down") {
    return translate(
      "链上节点暂不可用，列表仍可读；报价稍后自动重试",
      "Chain RPC unavailable — list OK; quotes will retry"
    );
  }
  const raw = String(
    err && typeof err === "object" && "message" in err
      ? err.message
      : err || ""
  ).trim();
  if (!raw) {
    return translate("请求失败，稍后重试", "Request failed — retry shortly");
  }
  return translate("出错：" + raw, "Error: " + raw);
}

